import type { UIEventHandler } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useUnit } from 'effector-react'
import times from 'lodash/times'
import { useMutation } from 'react-query'

import { IconLoading } from '@/assets/icons/icon-loading'
import NotFoundImage from '@/assets/images/not-found.svg'
import { Button } from '@/components/ui/button'
import { $creatorId } from '@/models/creators.ts'
import ReloadIcon from '@/modules/chatting/icons/reload.svg'
import { listsService } from '@/modules/chatting/service/lists'
import type { ListUser } from '@/modules/chatting/types'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { handleError } from '@/utils/error'

import { useCollectionContext } from '../../CollectionProvider'
import type { CollectionsItem } from '../../CollectionsList/types'
import { SubscribedUserCard } from './CollectionUserCard/SubscribedUserCard/SubscribedUserCard'
import { UnsubscribedUserCard } from './CollectionUserCard/UnsubscribedUserCard'
import { UserCardSkeleton } from './CollectionUserCard/UserCardSkeleton'

interface Props {
  listId: CollectionsItem['id']
}

export const CollectionUsers = ({ listId }: Props) => {
  const {
    updateUserInCache,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingBase,
    users,
    fetchNextPage,
    isError,
    refetch,
    isFetching,
  } = useCollectionContext()
  const creatorId = useUnit($creatorId)
  const parentRef = useRef<HTMLDivElement>(null)
  const columns = useMemo(() => {
    return users.reduce<[ListUser[], ListUser[]]>(
      (acc, user, index) => {
        if ((index + 1) % 2 === 0) {
          acc[1].push(user)
        } else {
          acc[0].push(user)
        }

        return acc
      },
      [[], []],
    )
  }, [users])

  const canLoadMore = !!hasNextPage && !isFetchingNextPage
  const isLoading = isLoadingBase || (!users.length && canLoadMore)
  const isLastPageFailed = users.length > 0 && isError

  const pinMutation = useMutation(
    (user: ListUser) =>
      listsService.pinUser({
        creatorId: creatorId!,
        userId: user.id,
        listId,
      }),
    {
      onSuccess: (_, user) => {
        updateUserInCache({ ...user, isPinned: !user.isPinned })
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while pinning user')
      },
    },
  )

  const fetchMoreOnBottomReached = useCallback(
    (container: HTMLDivElement) => {
      const { scrollHeight, scrollTop, clientHeight } = container

      if (scrollHeight - scrollTop - clientHeight < 100 && canLoadMore) {
        fetchNextPage()
      }
    },
    [canLoadMore, fetchNextPage],
  )

  const handleScroll: UIEventHandler<HTMLDivElement> = (event) => {
    const container = event.target as HTMLDivElement

    if (container) {
      fetchMoreOnBottomReached(container)
    }
  }

  useEffect(() => {
    if (parentRef.current && canLoadMore) {
      fetchMoreOnBottomReached(parentRef.current)
    }
  }, [canLoadMore, fetchMoreOnBottomReached])

  const loadingView = times(2).map((num) => (
    <div
      className="flex flex-1 flex-col gap-4 md:max-w-[calc(50%-8px)]"
      key={num}
    >
      {times(2).map((num) => (
        <UserCardSkeleton key={num} />
      ))}
    </div>
  ))

  if (users.length === 0 && isError) {
    return (
      <div className="h-full flex items-center justify-center flex-col">
        <p className="text-center">Something went wrong while loading users</p>
        <Button
          className="mt-2 mx-auto block"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    )
  }

  if (users.length === 0 && !isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center space-y-4">
        <NotFoundImage
          width={80}
          height={80}
        />
        <div className="text-secondary-300 text-sm leading-6">
          No users with selected filters
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <div
        className="flex max-h-[600px] max-w-full gap-4 overflow-scroll pr-2 max-md:flex-col"
        onScroll={handleScroll}
        ref={parentRef}
      >
        {isLoading
          ? loadingView
          : columns.map((column, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className="flex flex-1 flex-col gap-4 md:max-w-[calc(50%-8px)]"
              >
                {column.map((user) =>
                  listId.startsWith('following') || !user.subscribedOnData ? (
                    <UnsubscribedUserCard
                      key={user.id}
                      user={user}
                      onClickPin={() => pinMutation.mutateAsync(user)}
                      isPinLoading={
                        pinMutation.isLoading &&
                        pinMutation.variables?.id === user.id
                      }
                    />
                  ) : (
                    <SubscribedUserCard
                      user={user}
                      key={user.id}
                      onClickPin={() => pinMutation.mutateAsync(user)}
                      isPinLoading={
                        pinMutation.isLoading &&
                        pinMutation.variables?.id === user.id
                      }
                    />
                  ),
                )}
              </div>
            ))}
      </div>
      {isFetchingNextPage && (
        <div className="bg-card absolute bottom-0 left-0 right-0 pt-4">
          <IconLoading className="mx-auto animate-spin" />
        </div>
      )}
      {isLastPageFailed && !isFetching && (
        <IconButton
          rounded
          icon={ReloadIcon}
          onClick={() => fetchNextPage()}
          className="absolute left-1/2 z-10 -translate-x-1/2 bg-white bottom-2 -rotate-90 p-2 rounded-full bg-card border dark:border-secondary-400"
        />
      )}
    </div>
  )
}
